import React, { FunctionComponent } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import {
  CareTeamSelectionForm,
  CareTeamSelectionFormProps,
} from '../../molecules/careTeamSelectionForm/CareTeamSelectionForm'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { ConfirmationModal } from '../confirmationModal/ConfirmationModal'

export interface ManageCareTeamOrganismProps extends Omit<CareTeamSelectionFormProps, 'testID'> {
  visible: boolean
  onConfirm: () => void
  onRequestClose: () => void
  confirmDisabled?: boolean
  confirmLoading?: boolean
  testID?: string
}

const messages = defineMessages({
  title: {
    defaultMessage: 'Manage neuropsych team roles',
    description: 'Title of the modal where a supervisor assigns providers to the neuropsych care team roles',
  },
  subtitle: {
    defaultMessage: "Confirm each provider serves the client's region before assigning.",
    description: 'Helper text under the title of the manage neuropsych care team modal',
  },
  confirm: {
    defaultMessage: 'Confirm',
    description: 'Button that saves the neuropsych care team assignments',
  },
  cancel: {
    defaultMessage: 'Cancel',
    description: 'Button that closes the manage neuropsych care team modal without saving',
  },
})

const Contents = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['24px'],
  width: '912px',
  maxWidth: '100%',
}))

export const ManageCareTeamOrganism: FunctionComponent<ManageCareTeamOrganismProps> = ({
  visible,
  onConfirm,
  onRequestClose,
  confirmDisabled,
  confirmLoading,
  testID = 'ManageCareTeamOrganism',
  ...formProps
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme() as ThemeType

  return (
    <ConfirmationModal
      visible={visible}
      testID={testID}
      width='960px'
      modalTitle={formatMessage(messages.title)}
      modalContents={
        <Contents>
          <BodyText color={colors.textSecondary} text={formatMessage(messages.subtitle)} />
          <CareTeamSelectionForm {...formProps} testID={`${testID}-form`} />
        </Contents>
      }
      confirmationButtonText={formatMessage(messages.confirm)}
      cancelButtonText={formatMessage(messages.cancel)}
      onConfirmationButtonPress={onConfirm}
      confirmButtonDisabled={confirmDisabled}
      confirmButtonLoading={confirmLoading}
      onRequestClose={onRequestClose}
    />
  )
}
